import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Sidebar } from "./Sidebar.js";
import { FloatingMiniPlayer } from "./FloatingMiniPlayer.js";

/**
 * Layout — on desktop/TV the sidebar is a fixed left rail; on a phone-width
 * viewport (<768px) it collapses into a slide-in drawer behind a hamburger.
 * Route changes auto-close the drawer.
 */
export function Layout(): JSX.Element {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen md:flex">
      {/* Desktop / TV: sidebar is a permanent left column */}
      <div className="hidden md:block">
        <Sidebar />
      </div>

      {/* Mobile: top bar with hamburger */}
      <header className="md:hidden sticky top-0 z-30 h-14 px-4 flex items-center gap-3 bg-lacquer/95 backdrop-blur border-b border-hairline">
        <button
          onClick={() => setDrawerOpen(true)}
          className="pill-ghost p-2"
          aria-label="Open menu"
        >
          <Menu size={20} />
        </button>
        <div className="flex flex-col leading-tight">
          <span className="font-display text-gold text-sm tracking-smallcaps">WALLACE</span>
          <span className="font-display text-parchment text-[9px] tracking-smallcaps">— TV —</span>
        </div>
      </header>

      {/* Mobile drawer (overlay) */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-ink/80 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative z-10 h-full bg-lacquer border-r border-hairline shadow-2xl animate-in slide-in-from-left">
            <button
              onClick={() => setDrawerOpen(false)}
              className="absolute top-3 right-3 pill-ghost p-1.5 z-10"
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
            <Sidebar />
          </div>
        </div>
      )}

      <main className="flex-1 px-4 py-6 md:px-10 md:py-10 max-w-[1400px] mx-auto w-full">
        <Outlet />
      </main>

      <FloatingMiniPlayer />
    </div>
  );
}
